/**
 * Consult booking: each option is a real submit button with sesami-id so the user's click (trusted)
 * opens the Sesami modal. We set the form variant on mousedown so the correct variant is selected
 * when Sesami runs; we prevent the form from actually submitting (add to cart).
 * @see https://sesami.dev/docs/sesami-experience/events/
 */
(function () {
  'use strict';

  var lastTriggeredFormId = null;
  var lastSesamiTagId = null;
  var consultAddToCartInProgress = false;

  function getForm(formId) {
    if (!formId) return null;
    return document.getElementById(formId) || document.querySelector('form[data-product-id][id="' + formId + '"]');
  }

  function getFormVariantId(form) {
    if (!form) return null;
    var input = form.querySelector('input[name="id"]');
    return input ? input.value : null;
  }

  function setFormVariant(form, variantId) {
    if (!form || !variantId) return;
    var inputs = form.querySelectorAll('input[name="id"]');
    inputs.forEach(function (input) {
      input.value = variantId;
      input.disabled = false;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    document.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail: { variant: { id: parseInt(variantId, 10) } } }));
  }

  function setVariantFromOption(option) {
    if (!option) return;
    var variantId = option.getAttribute('data-variant-id');
    var formId = option.getAttribute('data-product-form-id');
    if (!variantId || !formId) return;
    var form = getForm(formId);
    if (!form) return;
    setFormVariant(form, variantId);
    lastTriggeredFormId = formId;
  }

  // Inject button padding into each sesami-experience shadow root. Shadow roots may be created async, so we wait for the custom element and poll.
  // Keep the opener button label static (Sesami replaces it with selected date/time). Poll so we always overwrite.
  function injectSesamiButtonStyles() {
    var elements = document.querySelectorAll('sesami-experience');
    elements.forEach(function (el) {
      if (!el.shadowRoot) return;
      if (!el.shadowRoot.querySelector('style[data-consult-booking-override]')) {
        var style = document.createElement('style');
        style.setAttribute('data-consult-booking-override', '');
        style.textContent = 'button:hover { background: #deceb4 !important; color: rgb(48,46,38) !important; } button { transition: all 0.2s ease-in-out !important; background: rgb(48,46,38) !important; letter-spacing: 0.05em !important; font-weight: 500 !important; text-transform: uppercase !important; width: auto !important; padding: 1.5rem 4rem; border-radius: 0 !important; font-family: "Work Sans", sans-serif; font-size: 16px !important; }';
        el.shadowRoot.appendChild(style);
      }
      applyStaticButtonLabel(el);
    });
  }
  function applyStaticButtonLabel(el) {
    var label = el.getAttribute('button-label');
    if (!label) return;
    var button = el.shadowRoot.querySelector('button');
    if (!button) return;
    if (button.textContent !== label) button.textContent = label;
  }
  function startStaticButtonLabelPolling() {
    if (window._consultBookingLabelPolling) return;
    window._consultBookingLabelPolling = setInterval(function () {
      // Shadow roots for <sesami-experience> can be created asynchronously.
      // Polling both styles and opener label guarantees consistent appearance.
      injectSesamiButtonStyles();
    }, 350);
  }
  function scheduleInjection() {
    injectSesamiButtonStyles();
    [0, 100, 300, 600, 1200].forEach(function (delay) {
      setTimeout(injectSesamiButtonStyles, delay);
    });
    startStaticButtonLabelPolling();
  }

  // Run as early as possible, but also keep reacting when Sesami loads/defines.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInjection);
  } else {
    scheduleInjection();
  }
  if (typeof customElements !== 'undefined' && customElements.whenDefined) {
    customElements.whenDefined('sesami-experience').then(scheduleInjection);
  }
  window.addEventListener('sesami:loaded', scheduleInjection);

  // Catch sesami-experience elements added later (e.g. dynamic sections)
  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      var hasSesami = mutations.some(function (m) {
        return [].some.call(m.addedNodes || [], function (n) {
          return (n.nodeType === 1 && (n.tagName === 'SESAMI-EXPERIENCE' || n.querySelector && n.querySelector('sesami-experience')));
        });
      });
      if (hasSesami) setTimeout(injectSesamiButtonStyles, 200);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Set variant before the click so Sesami sees the correct variant when the modal opens (mousedown + pointerdown for touch)
  function getConsultOptionFromEvent(e) {
    if (!e) return null;

    // If the click originates inside Sesami Shadow DOM, `e.target.closest(...)` may fail.
    // `composedPath()` includes nodes across the shadow boundary, so we can find the host wrapper reliably.
    if (typeof e.composedPath === 'function') {
      var path = e.composedPath();
      for (var i = 0; i < path.length; i++) {
        var node = path[i];
        if (node && node.nodeType === 1 && node.hasAttribute && node.hasAttribute('data-consult-option')) return node;
      }
    }

    if (e.target && typeof e.target.closest === 'function') {
      return e.target.closest('[data-consult-option]');
    }

    return null;
  }

  document.addEventListener('mousedown', function (e) {
    var option = getConsultOptionFromEvent(e);
    if (option) setVariantFromOption(option);
  }, true);
  document.addEventListener('pointerdown', function (e) {
    var option = getConsultOptionFromEvent(e);
    if (option) setVariantFromOption(option);
  }, true);
  // Some browsers/components react on `click` rather than pointerdown.
  document.addEventListener('click', function (e) {
    var option = getConsultOptionFromEvent(e);
    if (option) setVariantFromOption(option);
  }, true);

  // Prevent submit only when a button with data-consult-option submitted (we no longer have those; Sesami experience may submit the form)
  document.addEventListener('submit', function (e) {
    var submitter = e.submitter || null;
    if (submitter && submitter.getAttribute && submitter.getAttribute('data-consult-option') !== null) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  // Sesami events: react when modal opens/closes or booking completes (per variant)
  // https://sesami.dev/docs/sesami-experience/events/
  window.addEventListener('sesami:modal:opened', function (ev) {
    var tagId = ev.detail && ev.detail.tagId;
    lastSesamiTagId = tagId || null;
    var form = lastTriggeredFormId ? getForm(lastTriggeredFormId) : document.querySelector('form[data-product-id]');
    if (form) {
      var variantId = getFormVariantId(form);
      document.dispatchEvent(new CustomEvent('consult:sesami:modal:opened', {
        detail: { variantId: variantId ? parseInt(variantId, 10) : null, tagId: tagId }
      }));
    }
  });

  window.addEventListener('sesami:modal:closed', function () {
    document.dispatchEvent(new CustomEvent('consult:sesami:modal:closed'));
  });

  function addConsultToCartAndOpenDrawer() {
    if (consultAddToCartInProgress) return;
    consultAddToCartInProgress = true;
    var form = lastTriggeredFormId ? getForm(lastTriggeredFormId) : document.querySelector('form.consult-booking-options__form');
    if (!form) { consultAddToCartInProgress = false; return; }
    var formData = new FormData();
    var variantInput = form.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) { consultAddToCartInProgress = false; return; }
    formData.append('id', variantInput.value);
    formData.append('quantity', '1');
    var wrapper = lastSesamiTagId ? document.getElementById('sesami-hidden-fields-' + lastSesamiTagId) : null;
    if (!wrapper) {
      var wrappers = form.querySelectorAll('[id^="sesami-hidden-fields-"]');
      for (var i = 0; i < wrappers.length; i++) {
        var inputs = wrappers[i].querySelectorAll('input[name], select[name], textarea[name]');
        var hasValue = false;
        for (var j = 0; j < inputs.length; j++) {
          if (inputs[j].value) { hasValue = true; break; }
        }
        if (hasValue) { wrapper = wrappers[i]; break; }
      }
    }
    if (wrapper) {
      var inputs = wrapper.querySelectorAll('input[name], select[name], textarea[name]');
      for (var k = 0; k < inputs.length; k++) {
        if (inputs[k].name && inputs[k].value !== undefined) formData.append(inputs[k].name, inputs[k].value);
      }
    }
    fetch('/cart/add.js', { method: 'POST', body: formData })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.status && data.status === 422) {
          consultAddToCartInProgress = false;
          if (typeof window.showToast === 'function') window.showToast(data.description || 'Could not add to cart', 'error');
          else alert(data.description || 'Could not add to cart');
          return;
        }
        consultAddToCartInProgress = false;
        var cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(function () {
        consultAddToCartInProgress = false;
        if (typeof window.showToast === 'function') window.showToast('Could not add to cart', 'error');
        else alert('Could not add to cart');
      });
  }

  window.addEventListener('sesami:booking:success', function (ev) {
    var form = lastTriggeredFormId ? getForm(lastTriggeredFormId) : document.querySelector('form[data-product-id]');
    var variantId = form ? getFormVariantId(form) : null;
    document.dispatchEvent(new CustomEvent('consult:sesami:booking:success', {
      detail: { appointmentId: ev.detail && ev.detail.appointmentId, variantId: variantId ? parseInt(variantId, 10) : null }
    }));
    addConsultToCartAndOpenDrawer();
  });

  window.addEventListener('sesami:reservation:success', function () {
    var form = lastTriggeredFormId ? getForm(lastTriggeredFormId) : document.querySelector('form[data-product-id]');
    var variantId = form ? getFormVariantId(form) : null;
    document.dispatchEvent(new CustomEvent('consult:sesami:reservation:success', {
      detail: { variantId: variantId ? parseInt(variantId, 10) : null }
    }));
    addConsultToCartAndOpenDrawer();
  });

  window.addEventListener('sesami:booking:failed', function (ev) {
    document.dispatchEvent(new CustomEvent('consult:sesami:booking:failed', { detail: { error: ev.detail && ev.detail.error } }));
  });

  window.addEventListener('sesami:reservation:failed', function (ev) {
    document.dispatchEvent(new CustomEvent('consult:sesami:reservation:failed', { detail: { error: ev.detail && ev.detail.error } }));
  });
})();

